
import { useCallback } from 'react';
import { UseTwilioCallLogsReturn } from '../../useTwilioCallLogs';

export const useCallEventHandlers = (
  callLogIdRef: React.MutableRefObject<number | null>,
  activeCallRef: React.MutableRefObject<any>,
  callLogActions: UseTwilioCallLogsReturn,
  updateCallStatus: (status: string, callSid?: string) => void,
  resetCall: () => void,
  stopStatusPolling: () => void,
  handleCallSidAvailable: (callSid: string) => Promise<void>
) => {
  const handleCallAccept = useCallback((call: any) => {
    console.log('✅ Call accepted - updating UI to connected');
    const currentSid = call.parameters?.CallSid;
    console.log('🔍 [DEBUG] Accept event SID:', currentSid);
    
    updateCallStatus('open', currentSid);
    
    // Ensure we have call SID handled
    if (currentSid) {
      handleCallSidAvailable(currentSid);
    }
    
    // Update call log for accept event
    if (callLogIdRef.current) {
      console.log('📝 Updating call log for ACCEPT event');
      callLogActions.updateCallProgress('open').catch(error => {
        console.error('❌ Error updating call progress for accept:', error);
      });
    }
  }, [updateCallStatus, callLogActions, handleCallSidAvailable]);

  const handleCallRinging = useCallback((call: any) => {
    console.log('📞 Call ringing - updating UI');
    const currentSid = call.parameters?.CallSid;
    console.log('🔍 [DEBUG] Ringing event SID:', currentSid);
    
    updateCallStatus('ringing', currentSid);
    
    // This is often the first time we get a Call SID
    if (currentSid) {
      handleCallSidAvailable(currentSid);
    }
    
    // Update call log for ringing event
    if (callLogIdRef.current) {
      console.log('📝 Updating call log for RINGING event');
      callLogActions.updateCallProgress('ringing').catch(error => {
        console.error('❌ Error updating call progress for ringing:', error);
      });
    }
  }, [updateCallStatus, callLogActions, handleCallSidAvailable]);

  const handleCallDisconnect = useCallback((call: any) => {
    console.log('📴 Call disconnected - finalizing log and resetting UI');
    const finalSid = call.parameters?.CallSid;
    console.log('🔍 [DEBUG] Disconnect event SID:', finalSid);
    
    // Clear active call reference immediately
    activeCallRef.current = null;
    
    // Stop status polling immediately to prevent multiple resets
    stopStatusPolling();
    
    if (callLogIdRef.current) {
      console.log(`📝 Finalizing call log ${callLogIdRef.current} for DISCONNECT event with SID: ${finalSid}`);
      callLogActions.finalizeCallLogInDb('completed').catch(error => {
        console.error('❌ Error finalizing call log for disconnect:', error);
      }).finally(() => {
        callLogIdRef.current = null;
      });
    } else {
      console.warn('⚠️ No call log ID to finalize on disconnect');
    }
    
    // Reset call state
    resetCall();
  }, [callLogActions, resetCall, stopStatusPolling]);

  const handleCallCancel = useCallback(() => {
    console.log('❌ Call cancelled - finalizing log and resetting UI');
    
    // Clear active call reference immediately
    activeCallRef.current = null;
    
    // Stop status polling immediately
    stopStatusPolling();
    
    if (callLogIdRef.current) {
      console.log(`📝 Finalizing call log ${callLogIdRef.current} for CANCEL event`);
      callLogActions.finalizeCallLogInDb('failed', 'Call was cancelled').catch(error => {
        console.error('❌ Error finalizing call log for cancel:', error);
      }).finally(() => {
        callLogIdRef.current = null;
      });
    }
    
    resetCall();
  }, [callLogActions, resetCall, stopStatusPolling]);

  const handleCallError = useCallback((error: any) => {
    console.error('❌ Call error - finalizing log and resetting UI:', error);
    
    // Clear active call reference immediately
    activeCallRef.current = null;
    
    // Stop status polling immediately
    stopStatusPolling();
    
    if (callLogIdRef.current) {
      console.log(`📝 Finalizing call log ${callLogIdRef.current} for ERROR event`);
      callLogActions.finalizeCallLogInDb('failed', `Call error: ${error.message || 'Unknown error'}`).catch(logError => {
        console.error('❌ Error finalizing call log for error:', logError);
      }).finally(() => {
        callLogIdRef.current = null;
      });
    }
    
    resetCall();
  }, [callLogActions, resetCall, stopStatusPolling]);

  const handleCallStatus = useCallback((call: any, status: string) => {
    console.log('🔄 Call status changed to:', status);
    const statusSid = call.parameters?.CallSid;
    console.log('🔍 [DEBUG] Status event SID:', statusSid);
    
    updateCallStatus(status, statusSid);
    
    // Ensure we handle Call SID if this is the first time we see it
    if (statusSid) {
      handleCallSidAvailable(statusSid);
    }
    
    // Update call log for every status change
    if (callLogIdRef.current) {
      console.log(`📝 Updating call log for STATUS CHANGE event: ${status}`);
      callLogActions.updateCallProgress(status).catch(error => {
        console.error('❌ Error updating call progress for status change:', error);
      });
    }
  }, [updateCallStatus, callLogActions, handleCallSidAvailable]);

  return {
    handleCallAccept,
    handleCallRinging,
    handleCallDisconnect,
    handleCallCancel,
    handleCallError,
    handleCallStatus
  };
};
